import {
    Component,
    computed,
    DestroyRef,
    inject,
    model,
    ModelSignal,
    OnInit,
    Signal,
    signal,
    viewChild,
    ViewEncapsulation,
    WritableSignal,
} from '@angular/core';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * consts
 */
import { UnitsConst, UnitsTranslateMapper } from './consts';

/**
 * services
 */
import { UnitService } from './services';

/**
 * models
 */
import { IUnitModel, IUnitChildModel, IUnitBaseModel } from './models';

/**
 * components
 */
import { UnitDetailsComponent, UnitFormComponent } from './components';

/**
 * shared
 */
import { BadgeDirective } from '@shared/directives';
import { CardComponent } from '@sharedComponents/card/card.component';
import { ModalComponent } from '@sharedComponents/modal/modal.component';
import { ButtonDirective } from '@sharedDirectives/button/button.directive';
import { TooltipDirective } from "@shared/directives/tooltip/tooltip.directive";
import { TreeComponent, ITreeNode, patchTreeNode } from '@shared/components/tree';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';
import { SpinKitWaveComponent } from '@sharedComponents/spinner/spinkit/components/spin-kit-wave/spin-kit-wave.component';
import { ListGroupDirective } from '@shared/directives/list-groupe/list-group.directive';
import { ListGroupItemDirective } from '@shared/directives/list-groupe/list-group-item.directive';
import { StopPropagationDirective } from "@shared/directives/stop-propagation/stop-propagation.directive";
import { WavesEffectDirective } from '@shared/directives/node-waves/waves-effect.directive';

/**
 * Displays the units tree and a details panel for the selected unit.
 * Hosts add / edit / delete modals backed by {@link UnitFormComponent}.
 */
@Component({
    selector: 'app-units',
    templateUrl: './units.component.html',
    styleUrl: './units.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [
        /**
         * libs
         */
        TranslatePipe,
        /**
         * directives
         */
        ButtonDirective,
        BadgeDirective,
        TooltipDirective,
        TemplateDirective,
        ListGroupDirective,
        WavesEffectDirective,
        ListGroupItemDirective,
        StopPropagationDirective,
        /**
         * shared
         */
        TreeComponent,
        CardComponent,
        ModalComponent,
        SpinKitWaveComponent,
        /**
         * feature
         */
        UnitFormComponent,
        UnitDetailsComponent,
    ],
})
export class UnitsComponent implements OnInit {

    // ─── Services ─────────────────────────────────────────────────────────────

    /** @private Unit HTTP service. */
    private readonly _unitService = inject(UnitService);

    /** @private Destroy reference for RxJS stream cleanup. */
    private readonly _destroyRef  = inject(DestroyRef);

    // ─── Constants / keys ─────────────────────────────────────────────────────

    /** Route and configuration constants for the Units module. */
    public readonly UNITS_CONST       = UnitsConst;

    /** Translation key mapper exposed to the template. */
    public readonly TRANSLATE_KEY_MAP = UnitsTranslateMapper;

    // ─── View children ────────────────────────────────────────────────────────

    /** Reference to the embedded {@link UnitFormComponent} instance. */
    public readonly formRef = viewChild.required(UnitFormComponent);

    // ─── Tree state ───────────────────────────────────────────────────────────

    /** Raw first-level unit models returned by the API. */
    private readonly _units: WritableSignal<IUnitModel[]> = signal([]);

    /** Flat list of tree nodes rendered by `<app-tree>`. */
    public readonly treeNodes: WritableSignal<ITreeNode<IUnitModel | IUnitChildModel>[]> = signal([]);

    /** Computed root unit (global_type === true). Null before data loads. */
    public readonly rootUnit: Signal<IUnitModel | null> = computed(() =>
        this._units().find(u => u.type?.global_type === true) ?? null
    );

    /** Currently selected tree node; drives the details panel. */
    public readonly selectedNode: ModelSignal<ITreeNode<IUnitModel> | null> = model<ITreeNode<IUnitModel> | null>(null);

    /** True while the first-level units are being fetched. */
    public readonly isLoadingTree: WritableSignal<boolean> = signal(false);

    // ─── Details panel state ──────────────────────────────────────────────────

    /** Derived from {@link selectedNode}; null when nothing is selected. */
    public readonly selectedUnitDetails: Signal<IUnitModel | null> = computed(() =>
        this.selectedNode()?.data ?? null
    );

    // ─── Modal state ──────────────────────────────────────────────────────────

    /** Controls visibility of the add / edit form modal. */
    public readonly formModalVisible: ModelSignal<boolean>   = model<boolean>(false);

    /** Controls visibility of the delete-confirmation modal. */
    public readonly deleteModalVisible: ModelSignal<boolean> = model<boolean>(false);

    /** Unit pending deletion — holds the model so the template can call badge getters. */
    public pendingDeleteUnit: IUnitModel | IUnitChildModel | null = null;

    /** True while a delete (or other mutating) call is in flight. */
    public readonly isLoadingAction: WritableSignal<boolean> = signal(false);

    // ─── Lifecycle ────────────────────────────────────────────────────────────

    /** @inheritdoc */
    public ngOnInit(): void {
        this.loadFirstLevel();
    }

    /** Fetches the first-level units and populates {@link treeNodes}. */
    public loadFirstLevel(): void {
        this.isLoadingTree.set(true);
        this._unitService
            .getFirstLevel()
            .pipe(
                finalize(() => this.isLoadingTree.set(false)),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe((units) => {
                this._units.set(units);

                if (units.length > 0) {
                    const withoutRoot = units.filter(u => !u.type?.global_type);
                    this.treeNodes.set(withoutRoot.map(u => this._toTreeNode(u)));
                }

                if (this.treeNodes().length > 0 && this.selectedNode() == null) {
                    this.selectedNode.set(this.treeNodes()[0] as ITreeNode<IUnitModel>);
                }
            });
    }

    // ─── Tree actions ─────────────────────────────────────────────────────────

    /** Lazy-loads children of the expanded node on demand. */
    public onLazyLoad(event: { node: ITreeNode }): void {
        const { key, children } = event.node;

        if (children) return;

        event.node.loading  = true;
        event.node.expanded = true;

        this._unitService
            .getChildren(key)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (models) => {
                    const childNodes = models.map(m => this._toTreeNode(m));
                    this.treeNodes.update(nodes =>
                        patchTreeNode(nodes, key, { children: childNodes, loading: false })
                    );
                },
                error: () => {
                    this.treeNodes.update(nodes =>
                        patchTreeNode(nodes, key, { loading: false })
                    );
                },
            });
    }

    // ─── Form actions ─────────────────────────────────────────────────────────

    /** Opens the add-unit modal pre-configured for the given parent. */
    public showAddUnitForm(unit?: IUnitModel | IUnitChildModel | null): void {
        this.formRef().initCreate(unit ?? null);
        this.formModalVisible.set(true);
    }

    /** Opens the edit modal pre-populated with the given unit. */
    public showEditForm(unit: IUnitModel | IUnitChildModel): void {
        this.formRef().initEdit(unit);
        this.formModalVisible.set(true);
    }

    /** Cancels the form and hides the modal. */
    public closeFormModal(): void {
        this.formRef().cancel();
        this.formModalVisible.set(false);
    }

    /** Called when the form emits a successful submit; refreshes the tree. */
    public onFormSubmitted(_id: string): void {
        this.formModalVisible.set(false);
        this.loadFirstLevel();
    }

    // ─── Delete actions ───────────────────────────────────────────────────────

    /** Stores the unit to delete and opens the confirmation modal. */
    public confirmDelete(unit: IUnitModel | IUnitChildModel): void {
        this.pendingDeleteUnit = unit;
        this.deleteModalVisible.set(true);
    }

    /** Sends the delete API call for {@link pendingDeleteUnit}. */
    public onDeleteConfirmed(): void {
        const id = this.pendingDeleteUnit?.id;
        if (id == null) return;

        this.isLoadingAction.set(true);

        this._unitService
            .delete(id)
            .pipe(
                finalize(() => {
                    this.isLoadingAction.set(false);
                    this.deleteModalVisible.set(false);
                    this.pendingDeleteUnit = null;
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe(() => {
                if (this.selectedNode()?.key === id) {
                    this.selectedNode.set(null);
                }
                this.loadFirstLevel();
            });
    }

    // ─── Private helpers ──────────────────────────────────────────────────────

    /** Maps a unit model to a flat {@link ITreeNode} for the tree component. */
    private _toTreeNode<TModel extends IUnitBaseModel>(model: TModel): ITreeNode<TModel> {
        return {
            key:      model.id,
            label:    model.name,
            leaf:     !model.hasChildren,
            data:     model,
            expanded: false,
            loading:  false,
        };
    }

}
